import { signalRService } from './signalRService';

export interface SignalRNotification {
    id: string;
    title: string;
    description: string;
    icon?: string;
    notificationType: string;
    priority: string;
    isRead: boolean;
    creationTime: string;
    actionUrl?: string;
}

export interface MenuCounts {
    pendingRequests: number;
    todaysAppointments: number;
    unreadNotifications: number;
    unreadMessages: number;
    overdueTasks: number;
    unapprovedBookings: number;
    cancelledAppointments: number;
    requestsLast24Hours: number;
    lastUpdated: string;
}

export interface GlobalAnnouncement {
    title: string;
    message: string;
    tenantId: string;
    timestamp: string;
}

class SignalRNotificationService {
    private onNotificationsChangedCallbacks: Array<() => void> = [];
    private onUnreadCountUpdateCallbacks: Array<(count: number) => void> = [];
    private onMenuCountsUpdateCallbacks: Array<(counts: MenuCounts) => void> = [];
    private onMenuCountsRefreshNeededCallbacks: Array<() => void> = [];
    private onGlobalAnnouncementCallbacks: Array<(announcement: GlobalAnnouncement) => void> = [];
    private onConnectedCallbacks: Array<(info: any) => void> = [];
    private onErrorCallbacks: Array<(error: string) => void> = [];

    private initializingPromise: Promise<void> | null = null;

    async initialize(): Promise<void> {
        // If already initializing, return the in-flight promise to avoid races
        if (this.initializingPromise) {
            return this.initializingPromise;
        }

        // If the underlying connection is already up, just re-wire listeners
        if (signalRService.isConnected()) {
            this.setupEventListeners();
            return;
        }

        this.initializingPromise = (async () => {
            try {
                await signalRService.connect();
                this.setupEventListeners();
            } finally {
                this.initializingPromise = null;
            }
        })();

        return this.initializingPromise;
    }

    private setupEventListeners(): void {
        const connection = signalRService.getConnection();
        if (!connection) {
            console.error('SignalR connection not available');
            return;
        }

        // Remove any existing listeners first
        connection.off('NotificationsChanged');
        connection.off('UnreadCountUpdate');
        connection.off('MenuCountsUpdate');
        connection.off('MenuCountsRefreshNeeded');
        connection.off('GlobalAnnouncement');
        connection.off('Connected');
        connection.off('connected'); // lowercase variant from server
        connection.off('Error');

        // Set up new listeners
        // NotificationsChanged event - triggers data refresh
        connection.on('NotificationsChanged', (data: any) => {
            console.log('Received notifications changed event:', data);
            // Call all registered callbacks
            this.onNotificationsChangedCallbacks.forEach(callback => {
                try {
                    callback();
                } catch (error) {
                    console.error('Error in NotificationsChanged callback:', error);
                }
            });
        });

        connection.on('UnreadCountUpdate', (count: number) => {
            console.log('Received unread count update:', count);
            // Call all registered callbacks
            this.onUnreadCountUpdateCallbacks.forEach(callback => {
                try {
                    callback(count);
                } catch (error) {
                    console.error('Error in UnreadCountUpdate callback:', error);
                }
            });
        });

        connection.on('MenuCountsUpdate', (counts: any) => {
            console.log('Received menu counts update:', counts);
            // Call all registered callbacks
            this.onMenuCountsUpdateCallbacks.forEach(callback => {
                try {
                    callback(counts);
                } catch (error) {
                    console.error('Error in MenuCountsUpdate callback:', error);
                }
            });
        });

        connection.on('MenuCountsRefreshNeeded', () => {
            this.onMenuCountsRefreshNeededCallbacks.forEach(callback => {
                try {
                    callback();
                } catch (error) {
                    console.error('Error in MenuCountsRefreshNeeded callback:', error);
                }
            });
        });

        connection.on('GlobalAnnouncement', (announcement: any) => {
            console.log('Received global announcement:', announcement);
            this.onGlobalAnnouncementCallbacks.forEach(callback => {
                try {
                    callback(announcement);
                } catch (error) {
                    console.error('Error in GlobalAnnouncement callback:', error);
                }
            });
        });

        connection.on('Connected', (info: any) => {
            console.log('SignalR connection established:', info);
            this.onConnectedCallbacks.forEach(callback => {
                try {
                    callback(info);
                } catch (error) {
                    console.error('Error in Connected callback:', error);
                }
            });
        });

        // Also listen for lowercase 'connected' event from server
        connection.on('connected', (info: any) => {
            console.log('SignalR connection established (lowercase event):', info);
            this.onConnectedCallbacks.forEach(callback => {
                try {
                    callback(info);
                } catch (error) {
                    console.error('Error in connected callback:', error);
                }
            });
        });

        connection.on('Error', (error: string) => {
            console.error('SignalR error:', error);
            this.onErrorCallbacks.forEach(callback => {
                try {
                    callback(error);
                } catch (error) {
                    console.error('Error in Error callback:', error);
                }
            });
        });
    }

    setOnNotificationsChanged(callback: () => void): void {
        if (!this.onNotificationsChangedCallbacks.includes(callback)) {
            this.onNotificationsChangedCallbacks.push(callback);
        }
    }

    setOnUnreadCountUpdate(callback: (count: number) => void): void {
        if (!this.onUnreadCountUpdateCallbacks.includes(callback)) {
            this.onUnreadCountUpdateCallbacks.push(callback);
        }
    }

    setOnMenuCountsUpdate(callback: (counts: MenuCounts) => void): void {
        if (!this.onMenuCountsUpdateCallbacks.includes(callback)) {
            this.onMenuCountsUpdateCallbacks.push(callback);
        }
    }

    setOnMenuCountsRefreshNeeded(callback: () => void): void {
        if (!this.onMenuCountsRefreshNeededCallbacks.includes(callback)) {
            this.onMenuCountsRefreshNeededCallbacks.push(callback);
        }
    }

    setOnGlobalAnnouncement(callback: (announcement: GlobalAnnouncement) => void): void {
        if (!this.onGlobalAnnouncementCallbacks.includes(callback)) {
            this.onGlobalAnnouncementCallbacks.push(callback);
        }
    }

    setOnConnected(callback: (info: any) => void): void {
        if (!this.onConnectedCallbacks.includes(callback)) {
            this.onConnectedCallbacks.push(callback);
        }
    }

    setOnError(callback: (error: string) => void): void {
        if (!this.onErrorCallbacks.includes(callback)) {
            this.onErrorCallbacks.push(callback);
        }
    }

    removeOnNotificationsChanged(callback: () => void): void {
        this.onNotificationsChangedCallbacks = this.onNotificationsChangedCallbacks.filter(c => c !== callback);
    }

    removeOnUnreadCountUpdate(callback: (count: number) => void): void {
        this.onUnreadCountUpdateCallbacks = this.onUnreadCountUpdateCallbacks.filter(c => c !== callback);
    }

    removeOnMenuCountsUpdate(callback: (counts: MenuCounts) => void): void {
        this.onMenuCountsUpdateCallbacks = this.onMenuCountsUpdateCallbacks.filter(c => c !== callback);
    }

    removeOnMenuCountsRefreshNeeded(callback: () => void): void {
        this.onMenuCountsRefreshNeededCallbacks = this.onMenuCountsRefreshNeededCallbacks.filter(c => c !== callback);
    }

    removeOnGlobalAnnouncement(callback: (announcement: GlobalAnnouncement) => void): void {
        this.onGlobalAnnouncementCallbacks = this.onGlobalAnnouncementCallbacks.filter(c => c !== callback);
    }

    removeOnConnected(callback: (info: any) => void): void {
        this.onConnectedCallbacks = this.onConnectedCallbacks.filter(c => c !== callback);
    }

    removeOnError(callback: (error: string) => void): void {
        this.onErrorCallbacks = this.onErrorCallbacks.filter(c => c !== callback);
    }

    setOnConnectionLost(callback: () => void): void {
        signalRService.setOnConnectionLost(callback);
    }

    async refreshCounts(): Promise<void> {
        // Pass the current tenant + user ID so the hub scopes correctly during impersonation
        let tenantId: string | null = null;
        let userId: string | null = null;
        try {
            const config = JSON.parse(localStorage.getItem('app_config') || '{}');
            tenantId = config?.currentUser?.tenantId ?? null;
            userId = config?.currentUser?.id ?? null;
        } catch { /* ignore */ }
        await signalRService.invoke('RefreshCounts', tenantId, userId);
    }

    async markNotificationAsRead(notificationId: string): Promise<void> {
        await signalRService.invoke('MarkNotificationAsRead', notificationId);
    }

    isConnected(): boolean {
        return signalRService.isConnected();
    }

    async disconnect(): Promise<void> {
        // Reset the initialize guard so a future initialize() call is allowed
        this.initializingPromise = null;
        await signalRService.disconnect();
    }
}

export const signalRNotificationService = new SignalRNotificationService();
